import { useState } from "react"
import { Navigate, useNavigate } from "react-router-dom"
import { ArrowRight, Loader2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { AuthLayout } from "@/components/auth-layout"
import { AuthCard, AuthHeading, LogoMark } from "@/components/auth-card"
import { AuthField, AuthErrorBanner } from "@/components/auth-field"
import { useSession, type SessionUser } from "@/hooks/use-session"

export function OnboardingPage() {
  const session = useSession()
  if (session.status === "loading") return null
  if (session.status === "unauthenticated") return <Navigate to="/login" replace />
  if (session.user.onboarding_complete) return <Navigate to="/" replace />
  return <OnboardingForm user={session.user} />
}

function slugify(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
}

function OnboardingForm({ user }: { user: SessionUser }) {
  const navigate = useNavigate()
  const [name, setName] = useState(user.organization_name ?? "")
  const [slug, setSlug] = useState(user.organization_slug ?? "")
  const [slugEdited, setSlugEdited] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const onNameChange = (value: string) => {
    setName(value)
    if (!slugEdited) setSlug(slugify(value))
  }

  const onSlugChange = (value: string) => {
    setSlugEdited(true)
    setSlug(slugify(value))
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (submitting) return
    setError(null)
    setSubmitting(true)
    try {
      const res = await fetch("/api/v1/saas/auth/organization", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name: name.trim(), slug }),
      })
      const body = (await res.json()) as { ok?: boolean; error?: string }
      if (!res.ok) {
        setError(body.error ?? "Couldn't save your workspace. Try again.")
        setSubmitting(false)
        return
      }
      // Re-bootstrap so AuthGate re-reads the session and opens the app.
      navigate("/", { replace: true })
    } catch {
      setError("Couldn't reach the server. Try again in a moment.")
      setSubmitting(false)
    }
  }

  const slugValid = slug.length >= 3

  return (
    <AuthLayout>
      <LogoMark />
      <AuthCard>
        <div className="space-y-7">
          <AuthHeading
            eyebrow="One last step"
            title="Name your workspace"
            description="This is how your team and your webhook URLs are identified. You can change it later in settings."
          />

          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            <AuthField label="Workspace name">
              <Input
                value={name}
                onChange={(e) => onNameChange(e.target.value)}
                placeholder="Acme Inc."
                autoFocus
                maxLength={100}
              />
            </AuthField>

            <AuthField
              label="Workspace URL"
              error={
                slug.length > 0 && !slugValid
                  ? "At least 3 characters (letters, numbers, hyphens)"
                  : undefined
              }
            >
              <div className="flex items-center rounded-md border border-input bg-transparent focus-within:border-ring">
                <span className="pl-3 pr-1 text-sm text-muted-foreground/70 select-none">
                  hivehook.com/
                </span>
                <input
                  value={slug}
                  onChange={(e) => onSlugChange(e.target.value)}
                  placeholder="acme"
                  spellCheck={false}
                  className="flex-1 bg-transparent py-2 pr-3 text-sm outline-none placeholder:text-muted-foreground/50"
                />
              </div>
            </AuthField>

            {error && <AuthErrorBanner message={error} />}

            <Button
              type="submit"
              disabled={submitting || name.trim().length === 0 || !slugValid}
              className="group w-full bg-rose-600 text-white shadow-lg shadow-rose-500/20 transition-all hover:bg-rose-500 hover:shadow-rose-500/30 disabled:bg-rose-600/60"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Creating workspace
                </>
              ) : (
                <>
                  Create workspace
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </>
              )}
            </Button>
          </form>
        </div>
      </AuthCard>
    </AuthLayout>
  )
}
